import { ReactElement } from 'react'

import { Label } from 'components/v2/Label'
import { BusinessClientAutocomplete } from 'features/BusinessClients/components/BusinessClientAutocomplete'
import {
  BusinessClientSuggestion,
  BusinessClientSuggestionVertical,
  CompanySuggestion,
  CompanyV2Vertical
} from 'gql/generated/graphql'
import styled from 'styled-components'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { CompanyAutocomplete } from 'features/Companies/components/CompanyAutocomplete'
import { device } from 'utils/style'
import { getQueryString } from 'utils/route'
import { RoutesPlanningSecondaryFilters } from 'features/RoutesPlanning/views/Index/SecondaryFilters'
import { IRoutesPlanningFiltersModel } from 'features/RoutesPlanning/models'

const Container = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(3, min-content);
`

const FilterContainer = styled.div<{ justifySelf?: string }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
  white-space: nowrap;
  justify-self: ${({ justifySelf }): string => justifySelf || 'flex-start'};

  @media ${device.tablet} {
    width: auto;
  }

  input {
    width: 140px;
  }
`

const SecondaryFiltersContainer = styled.div`
  white-space: nowrap;
`

const messages = defineMessages({
  selectCompany: {
    id: 'routesPlanning.selectCompany.placeholder',
    defaultMessage: 'Select company'
  },
  selectBusinessClient: {
    id: 'routesPlanning.selectBusinessClient.placeholder',
    defaultMessage: 'Select business client'
  }
})

interface IRoutePlanningMainFiltersProps {
  businessClient: BusinessClientSuggestion | null
  setBusinessClient: (businessClient: BusinessClientSuggestion) => void
  company: CompanySuggestion | null
  setCompany: (company: CompanySuggestion) => void
  secondaryFilters: IRoutesPlanningFiltersModel
}

export const RoutePlanningMainFilters = ({
  businessClient,
  setBusinessClient,
  company,
  setCompany,
  secondaryFilters
}: IRoutePlanningMainFiltersProps): ReactElement => {
  const intl = useIntl()
  const navigate = useNavigate()

  return (
    <Container>
      <FilterContainer>
        <Label>
          <FormattedMessage id="routesPlanning.company.label" defaultMessage="Company" />
        </Label>
        <CompanyAutocomplete
          id="company"
          placeholder={intl.formatMessage(messages.selectCompany)}
          value={company?.name || ''}
          onChange={(company): void => {
            setCompany(company)
            navigate(`/routes-planning?companyId=${company.id}`)
          }}
          verticals={[CompanyV2Vertical.Team, CompanyV2Vertical.Travel]}
        />
      </FilterContainer>
      <FilterContainer>
        <Label>
          <FormattedMessage id="routesPlanning.businessClient.label" defaultMessage="Business client" />
        </Label>
        <BusinessClientAutocomplete
          id="businessClient"
          placeholder={intl.formatMessage(messages.selectBusinessClient)}
          value={businessClient?.name || ''}
          onChange={(businessClient): void => {
            setBusinessClient(businessClient)
            const queryString = getQueryString({
              businessClientId: businessClient.id,
              companyId: company?.id
            })
            navigate(`/routes-planning?${queryString}`)
          }}
          verticals={[BusinessClientSuggestionVertical.Team]}
          companyId={company?.id}
        />
      </FilterContainer>
      <SecondaryFiltersContainer>
        <RoutesPlanningSecondaryFilters
          filters={secondaryFilters}
          businessClientId={businessClient?.id}
          companyId={company?.id}
        />
      </SecondaryFiltersContainer>
    </Container>
  )
}
